// @ts-nocheck
import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_ENDPOINTS } from "../config/api";
import "./BrandMyLaptop.css";

interface Slot {
    slotId: string;
    section: "lid" | "palm_left" | "palm_right";
    name: string;
    dimensions: string;
    price: number;
    currency: string;
    isBooked: boolean;
    sponsor?: {
        companyName?: string;
        website?: string;
        logoUrl?: string;
        bookedAt?: string;
    };
}

interface Campaign {
    _id: string;
    title: string;
    description: string;
    destination: string;
    startDate?: string;
    endDate?: string;
    bookingOpenDate?: string;
    bookingCloseDate?: string;
    status: "open" | "extended" | "closed" | "completed";
    isExtended: boolean;
    slots: Slot[];
    proofOfWork?: {
        photos: string[];
        recapText: string;
        publishedAt?: string;
    };
}

export default function BrandMyLaptop() {
    const [viewMode, setViewMode] = useState<"lid" | "inside">("lid");
    const [loading, setLoading] = useState<boolean>(true);
    const [campaign, setCampaign] = useState<Campaign | null>(null);
    const [pastCampaigns, setPastCampaigns] = useState<Campaign[]>([]);
    
    // Booking Modal state
    const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
    const [companyName, setCompanyName] = useState<string>("");
    const [website, setWebsite] = useState<string>("");
    const [contactEmail, setContactEmail] = useState<string>("");
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string>("");
    const [bookingSubmitting, setBookingSubmitting] = useState<boolean>(false);
    const [bookingSuccess, setBookingSuccess] = useState<string>("");
    const [bookingError, setBookingError] = useState<string>("");

    const fetchCurrentCampaign = async () => {
        try {
            setLoading(true);
            const res = await axios.get(API_ENDPOINTS.BRAND_MY_LAPTOP.CURRENT);
            if (res.data && res.data.success) {
                setCampaign(res.data.activeCampaign);
                setPastCampaigns(res.data.pastCampaigns || []);
            }
        } catch (err) {
            console.error("Error fetching Brand My Laptop campaign:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCurrentCampaign();
    }, []);

    const handleSlotClick = (slot: Slot) => {
        if (slot.isBooked) {
            if (slot.sponsor?.website) {
                window.open(slot.sponsor.website, "_blank", "noopener,noreferrer");
            }
            return;
        }
        setSelectedSlot(slot);
        setBookingSuccess("");
        setBookingError("");
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setLogoFile(file);
            const reader = new FileReader();
            reader.onload = (uploadEvent) => {
                setLogoPreview(uploadEvent.target?.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleBookingSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!campaign || !selectedSlot) return;

        if (!companyName.trim()) {
            setBookingError("Please enter your company name.");
            return;
        }
        if (!website.trim()) {
            setBookingError("Please enter your website URL.");
            return;
        }
        if (!logoFile) {
            setBookingError("Please upload your brand sticker/logo image.");
            return;
        }

        setBookingSubmitting(true);
        setBookingError("");

        try {
            const formData = new FormData();
            formData.append("campaignId", campaign._id);
            formData.append("slotId", selectedSlot.slotId);
            formData.append("companyName", companyName);
            formData.append("website", website);
            formData.append("contactEmail", contactEmail);
            formData.append("logo", logoFile);

            const res = await axios.post(API_ENDPOINTS.BRAND_MY_LAPTOP.BOOK, formData, {
                headers: { "Content-Type": "multipart/form-data" }
            });

            if (res.data && res.data.success) {
                setBookingSuccess(res.data.message || "Spot booked successfully!");
                // Refresh campaign data
                await fetchCurrentCampaign();
                setTimeout(() => {
                    setSelectedSlot(null);
                    setCompanyName("");
                    setWebsite("");
                    setContactEmail("");
                    setLogoFile(null);
                    setLogoPreview("");
                    setBookingSuccess("");
                }, 2200);
            } else {
                setBookingError(res.data.message || "Could not book spot. Please try again.");
            }
        } catch (err: any) {
            console.error("Booking error:", err);
            setBookingError(err.response?.data?.message || "Something went wrong while booking.");
        } finally {
            setBookingSubmitting(false);
        }
    };

    // Filter slots by section
    const lidSlots = campaign?.slots.filter((s) => s.section === "lid") || [];
    const palmLeftSlots = campaign?.slots.filter((s) => s.section === "palm_left") || [];
    const palmRightSlots = campaign?.slots.filter((s) => s.section === "palm_right") || [];

    const bookedCount = campaign?.slots.filter((s) => s.isBooked).length || 0;
    const totalSlots = campaign?.slots.length || 16;

    const renderSlot = (slot: Slot) => {
        if (!slot) return null;
        if (slot.isBooked) {
            return (
                <div
                    key={slot.slotId}
                    className="bml-slot-card booked"
                    onClick={() => handleSlotClick(slot)}
                    title={`Sponsor: ${slot.sponsor?.companyName || "Booked"} - Click to visit website`}
                >
                    <img
                        src={slot.sponsor?.logoUrl?.startsWith("http") ? slot.sponsor.logoUrl : `${API_ENDPOINTS.AUTH.LOGIN.replace("/api/auth/login", "")}${slot.sponsor?.logoUrl}`}
                        alt={slot.sponsor?.companyName || "Sponsor Logo"}
                        className="bml-slot-sticker-img"
                    />
                    <div className="bml-sponsor-meta">
                        <div className="bml-sponsor-name">{slot.sponsor?.companyName}</div>
                        <div className="bml-sponsor-link-hint">
                            <span>Visit</span>
                            <i className="fi fi-rr-arrow-up-right" style={{ fontSize: 10 }}></i>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div
                key={slot.slotId}
                className="bml-slot-card available"
                onClick={() => handleSlotClick(slot)}
            >
                <div className="bml-slot-available-name">{slot.name}</div>
                <div className="bml-slot-dims">{slot.dimensions}</div>
                <div className="bml-slot-price-badge">
                    ${slot.price}
                </div>
                <div className="bml-slot-cta">
                    + Book Spot
                </div>
            </div>
        );
    };

    return (
        <div className="bml-container">
            {/* Hero Section */}
            <div className="bml-hero">
                <div className="bml-badge-row">
                    <span className="bml-live-dot"></span>
                    <span>Apple MacBook Pro 14" Silver Surface Branding</span>
                    <span>•</span>
                    <span>{bookedCount} / {totalSlots} Spots Taken</span>
                </div>
                <h1>
                    Brand My <span className="highlight">Laptop</span>.
                </h1>
                <p className="subtitle">
                    Your startup sticker travels with me across national conferences, builder stages, cafes, and creator hackathons on my Apple MacBook Pro 14". Instant booking — no bidding needed.
                </p>

                {/* Weekly Event / Trip Banner */}
                {campaign && (
                    <div className="bml-trip-banner">
                        <div className="bml-trip-info">
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                                <i className="fi fi-rr-plane-departure" style={{ color: "#2563eb", fontSize: 18 }}></i>
                                <h3>{campaign.title} ({campaign.destination})</h3>
                            </div>
                            <p>{campaign.description}</p>
                        </div>
                        <div>
                            {campaign.isExtended ? (
                                <span className="bml-status-pill extended">
                                    <i className="fi fi-rr-clock"></i> Booking Extended!
                                </span>
                            ) : campaign.status === "open" ? (
                                <span className="bml-status-pill open">
                                    <i className="fi fi-rr-check"></i> Booking Open (Mon – Fri)
                                </span>
                            ) : (
                                <span className="bml-status-pill closed">
                                    <i className="fi fi-rr-lock"></i> Booking Closed
                                </span>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* View Switcher: Lid vs Inside */}
            <div className="bml-switcher-container">
                <div className="bml-switcher">
                    <button
                        className={viewMode === "lid" ? "active" : ""}
                        onClick={() => setViewMode("lid")}
                    >
                        <span>Display Backside (Lid)</span>
                        <span className="bml-badge-count">8 Slots</span>
                    </button>
                    <button
                        className={viewMode === "inside" ? "active" : ""}
                        onClick={() => setViewMode("inside")}
                    >
                        <span>Inside (Palm Rest)</span>
                        <span className="bml-badge-count">8 Slots</span>
                    </button>
                </div>
            </div>

            {/* Laptop Interactive Canvas */}
            <div className="bml-laptop-viewport">
                {viewMode === "lid" ? (
                    <div className="bml-macbook-lid">
                        {/* Apple Logo in Center */}
                        <div className="bml-apple-logo-center">
                            <svg viewBox="0 0 170 170" width="56" height="56" fill="currentColor">
                                <path d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.19-2.12-9.97-3.17-14.34-3.17-4.58 0-9.49 1.05-14.75 3.17-5.26 2.13-9.5 3.24-12.74 3.35-4.35.13-9.16-1.9-14.42-6.08-3.7-3.04-7.59-7.71-11.65-14.01-6.1-9.49-10.9-20.15-14.41-31.98-3.52-11.83-5.28-22.95-5.28-33.37 0-14.36 3.6-26.43 10.8-36.21s16.63-14.77 28.3-14.97c4.68 0 9.8 1.15 15.35 3.45 5.56 2.3 9.38 3.52 11.45 3.67 1.84-.15 5.86-1.42 12.06-3.81 6.2-2.39 11.53-3.41 15.99-3.06 12.18.98 21.84 5.38 28.98 13.2-10.68 6.42-15.92 15.42-15.72 27 .2 9.04 3.73 16.7 10.59 22.98 6.86 6.28 14.99 9.8 24.39 10.56-2.6 7.6-5.88 15.1-9.84 22.5zM119.22 33.02c0-7.44 2.66-14.46 7.98-21.06 5.32-6.6 11.95-10.92 19.89-12.96.2 1.34.3 2.58.3 3.72 0 7.33-2.77 14.42-8.31 21.27-5.54 6.85-12.28 11.08-20.22 12.69-.53-1.22-.8-2.45-.8-3.66z" />
                            </svg>
                        </div>

                        {/* Top row: 3 slots */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
                            {renderSlot(lidSlots[0])}
                            {renderSlot(lidSlots[1])}
                            {renderSlot(lidSlots[2])}
                        </div>

                        {/* Middle row: 2 slots flanking Apple logo */}
                        <div className="bml-lid-middle-row" style={{ margin: "16px 0" }}>
                            <div>{renderSlot(lidSlots[3])}</div>
                            <div style={{ width: 120 }}></div>
                            <div>{renderSlot(lidSlots[4])}</div>
                        </div>

                        {/* Bottom row: 3 slots */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
                            {renderSlot(lidSlots[5])}
                            {renderSlot(lidSlots[6])}
                            {renderSlot(lidSlots[7])}
                        </div>
                    </div>
                ) : (
                    <div className="bml-macbook-inside">
                        {/* Keyboard Top representation */}
                        <div className="bml-keyboard-well">
                            <div className="bml-keyboard-preview">
                                <div className="bml-kb-row">
                                    {[...Array(14)].map((_, i) => <div key={i} className="bml-kb-key"></div>)}
                                </div>
                                <div className="bml-kb-row">
                                    {[...Array(14)].map((_, i) => <div key={i} className="bml-kb-key"></div>)}
                                </div>
                                <div className="bml-kb-row">
                                    {[...Array(13)].map((_, i) => <div key={i} className="bml-kb-key"></div>)}
                                </div>
                                <div className="bml-kb-row">
                                    {[...Array(12)].map((_, i) => <div key={i} className="bml-kb-key"></div>)}
                                </div>
                                <div className="bml-kb-row">
                                    <div className="bml-kb-key"></div>
                                    <div className="bml-kb-key"></div>
                                    <div className="bml-kb-key space"></div>
                                    <div className="bml-kb-key"></div>
                                    <div className="bml-kb-key"></div>
                                </div>
                            </div>
                        </div>

                        {/* Palm rest area: 4 left slots, Trackpad, 4 right slots */}
                        <div className="bml-palm-section">
                            {/* Left Palm Rest (2x2 grid) */}
                            <div className="bml-palm-grid">
                                {palmLeftSlots.map((slot) => renderSlot(slot))}
                            </div>

                            {/* Center Trackpad */}
                            <div className="bml-trackpad-area">
                                <i className="fi fi-rr-cursor" style={{ fontSize: 24, marginBottom: 6 }}></i>
                                <span>Force Touch Trackpad</span>
                            </div>

                            {/* Right Palm Rest (2x2 grid) */}
                            <div className="bml-palm-grid">
                                {palmRightSlots.map((slot) => renderSlot(slot))}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Past Brand Journeys & Proof of Work */}
            {pastCampaigns.length > 0 && (
                <div className="bml-history-section">
                    <h2>Past Brand Journeys & Proof of Work</h2>
                    <p className="section-sub">
                        Real-world proof! Here are previous trips where my MacBook Pro was fully stickered with our sponsor brands at major conferences and demo days.
                    </p>

                    <div className="bml-history-grid">
                        {pastCampaigns.map((past) => (
                            <div key={past._id} className="bml-trip-card">
                                {past.proofOfWork?.photos && past.proofOfWork.photos.length > 0 ? (
                                    <div className="bml-trip-photo-gallery">
                                        {past.proofOfWork.photos.map((photo, pIdx) => (
                                            <img
                                                key={pIdx}
                                                src={photo.startsWith("http") ? photo : `${API_ENDPOINTS.AUTH.LOGIN.replace("/api/auth/login", "")}${photo}`}
                                                alt={`Proof of laptop branding from ${past.destination}`}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div style={{ height: 160, background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8" }}>
                                        <i className="fi fi-rr-laptop" style={{ fontSize: 32 }}></i>
                                    </div>
                                )}
                                <div className="bml-trip-body">
                                    <h3>{past.title} ({past.destination})</h3>
                                    <div className="bml-trip-dates">
                                        <i className="fi fi-rr-calendar"></i>
                                        <span>
                                            {past.startDate ? new Date(past.startDate).toLocaleDateString() : "Trip Completed"}
                                        </span>
                                    </div>
                                    <p className="bml-trip-recap">
                                        {past.proofOfWork?.recapText || past.description}
                                    </p>

                                    {/* Past Sponsors */}
                                    <div className="bml-trip-sponsors">
                                        <span className="label">Featured Sponsors:</span>
                                        <div className="bml-trip-sponsor-badges">
                                            {past.slots
                                                .filter((s) => s.isBooked && s.sponsor?.companyName)
                                                .map((s, idx) => (
                                                    <a
                                                        key={idx}
                                                        href={s.sponsor?.website || "#"}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="bml-past-sponsor-chip"
                                                    >
                                                        {s.sponsor?.logoUrl && (
                                                            <img
                                                                src={s.sponsor.logoUrl.startsWith("http") ? s.sponsor.logoUrl : `${API_ENDPOINTS.AUTH.LOGIN.replace("/api/auth/login", "")}${s.sponsor.logoUrl}`}
                                                                alt=""
                                                            />
                                                        )}
                                                        <span>{s.sponsor?.companyName}</span>
                                                    </a>
                                                ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Instant Booking Modal */}
            {selectedSlot && (
                <div className="bml-modal-overlay" onClick={() => setSelectedSlot(null)}>
                    <div className="bml-modal-box" onClick={(e) => e.stopPropagation()}>
                        <button className="bml-modal-close" onClick={() => setSelectedSlot(null)}>
                            ✕
                        </button>
                        <div className="bml-modal-header">
                            <h2>Book Slot: {selectedSlot.name}</h2>
                            <p>Immediate booking — showcase your brand on my MacBook Pro 14".</p>
                        </div>

                        <div className="bml-slot-spec-banner">
                            <div>
                                <span style={{ fontSize: 12, color: "#64748b", display: "block" }}>Location</span>
                                <strong style={{ textTransform: "capitalize" }}>
                                    {selectedSlot.section.replace("_", " ")}
                                </strong>
                            </div>
                            <div>
                                <span style={{ fontSize: 12, color: "#64748b", display: "block" }}>Dimensions</span>
                                <strong>{selectedSlot.dimensions}</strong>
                            </div>
                            <div>
                                <span style={{ fontSize: 12, color: "#64748b", display: "block" }}>Price</span>
                                <strong style={{ color: "#2563eb", fontSize: 18 }}>
                                    ${selectedSlot.price}
                                </strong>
                            </div>
                        </div>

                        {bookingSuccess ? (
                            <div style={{ background: "#ecfdf5", color: "#065f46", padding: 18, borderRadius: 12, textAlign: "center", fontWeight: 600 }}>
                                <i className="fi fi-rr-check-circle" style={{ fontSize: 28, display: "block", marginBottom: 6 }}></i>
                                {bookingSuccess}
                            </div>
                        ) : (
                            <form onSubmit={handleBookingSubmit}>
                                {bookingError && (
                                    <div style={{ background: "#fef2f2", color: "#991b1b", padding: 10, borderRadius: 8, marginBottom: 14, fontSize: 13 }}>
                                        {bookingError}
                                    </div>
                                )}

                                <div className="bml-form-group">
                                    <label>Company / Startup Name *</label>
                                    <input
                                        type="text"
                                        placeholder="e.g. Acme AI"
                                        value={companyName}
                                        onChange={(e) => setCompanyName(e.target.value)}
                                        required
                                    />
                                </div>

                                <div className="bml-form-group">
                                    <label>Website URL (Visitors will be directed here) *</label>
                                    <input
                                        type="url"
                                        placeholder="https://yourcompany.com"
                                        value={website}
                                        onChange={(e) => setWebsite(e.target.value)}
                                        required
                                    />
                                </div>

                                <div className="bml-form-group">
                                    <label>Contact Email (For confirmation)</label>
                                    <input
                                        type="email"
                                        placeholder="founder@yourcompany.com"
                                        value={contactEmail}
                                        onChange={(e) => setContactEmail(e.target.value)}
                                    />
                                </div>

                                <div className="bml-form-group">
                                    <label>Sticker Logo Image (PNG / SVG recommended: {selectedSlot.dimensions}) *</label>
                                    <label className="bml-upload-box">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            style={{ display: "none" }}
                                            onChange={handleFileChange}
                                        />
                                        <i className="fi fi-rr-cloud-upload" style={{ fontSize: 24, color: "#2563eb", display: "block", marginBottom: 4 }}></i>
                                        <span style={{ fontSize: 13, color: "#475569" }}>
                                            {logoFile ? logoFile.name : "Click to select logo (transparent PNG/SVG recommended)"}
                                        </span>
                                    </label>

                                    {logoPreview && (
                                        <div className="bml-preview-wrapper">
                                            <span style={{ fontSize: 11, color: "#64748b", display: "block", marginBottom: 4 }}>Sticker Preview:</span>
                                            <img src={logoPreview} alt="Preview" />
                                        </div>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    className="bml-submit-btn"
                                    disabled={bookingSubmitting}
                                >
                                    {bookingSubmitting ? "Booking Slot..." : `Confirm & Book Instant Spot ($${selectedSlot.price})`}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
